import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  setDoc,
  increment,
  getDoc,
  getDocs,
  getDocsFromCache,
  limitToLast,
  startAfter,
  endBefore,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { apiClient } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Generates a deterministic room ID from two UIDs.
 * Sorting ensures the ID is the same regardless of who starts the chat.
 */
export function getRoomId(uid1, uid2) {
  return [String(uid1), String(uid2)].sort().join('_');
}

const BATCH_SIZE = 15;

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useChatRoom — manages a real-time Firestore chat session with pagination.
 *
 * - Loads the last 15 messages on init (one-time getDocs)
 * - Subscribes to only NEW messages via startAfter(newestDoc)
 * - Exposes loadMore() to fetch 15 older messages at a time (endBefore pagination)
 *
 * @param {string} currentUserUid  - The UID of the logged-in user (user?.id preferred).
 * @param {string} otherUserUid    - The UID of the conversation partner.
 * @param {object} receiverMeta    - Optional { name, avatar } for the partner.
 */
export function useChatRoom(currentUserUid, otherUserUid, receiverMeta = {}) {
  const [messages, setMessages]         = useState([]);
  const [status, setStatus]             = useState('idle'); // 'idle' | 'loading' | 'succeeded' | 'failed'
  const [error, setError]               = useState(null);
  const [roomId, setRoomId]             = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore]           = useState(false);

  const unsubscribeRef = useRef(null);
  const oldestDocRef   = useRef(null); // DocumentSnapshot of the oldest loaded message
  const newestDocRef   = useRef(null); // DocumentSnapshot of the newest loaded message

  // ── Detach listener helper ─────────────────────────────────────────────────
  const detachListener = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  }, []);

  // ── Initialize room then subscribe to real-time messages ───────────────────
  useEffect(() => {
    if (!currentUserUid || !otherUserUid) {
      setRoomId(null);
      setMessages([]);
      setStatus('idle');
      return;
    }

    let isMounted = true;
    detachListener();

    setStatus('loading');
    setMessages([]);
    setError(null);
    setRoomId(null);
    setHasMore(false);
    oldestDocRef.current = null;
    newestDocRef.current = null;

    const rid = getRoomId(currentUserUid, otherUserUid);

    const init = async () => {
      // ── Step 1 & 2: Background Sync (Non-blocking) ──────────────────────────
      (async () => {
        try {
          const roomRef = doc(db, 'chats', rid);
          console.log(`[useChatRoom] Background sync starting for room: "${rid}"`);

          const roomSnap = await getDoc(roomRef);
          if (!roomSnap.exists()) {
            await setDoc(roomRef, {
              participants: [String(currentUserUid), String(otherUserUid)].sort(),
              sender_uid:   String(currentUserUid),
              receiver_uid: String(otherUserUid),
              created_at:   new Date().toISOString(),
              unreadCounts: {
                [String(currentUserUid)]: 0,
                [String(otherUserUid)]: 0
              },
              last_message: '',
              last_time:    serverTimestamp(),
              ...(receiverMeta.name   ? { receiver_name:   receiverMeta.name }   : {}),
              ...(receiverMeta.avatar ? { receiver_avatar: receiverMeta.avatar } : {}),
            });
          } else {
            const data = roomSnap.data();
            if (!data.unreadCounts) {
              await updateDoc(roomRef, {
                unreadCounts: {
                  [String(currentUserUid)]: 0,
                  [String(otherUserUid)]: 0
                }
              });
            } else {
              // Reset unread count for current user
              await updateDoc(roomRef, {
                [`unreadCounts.${String(currentUserUid)}`]: 0
              });
            }
          }
        } catch (writeErr) {
          if (writeErr?.code !== 'permission-denied') {
            console.warn('[useChatRoom] Firestore room upsert failed:', writeErr?.code);
          }
        }

        try {
          const roomsResponse = await apiClient.get(API_ENDPOINTS.chats.rooms);
          const roomsList = roomsResponse.data?.data?.data || roomsResponse.data?.data || roomsResponse.data || [];
          const existsOnBackend = roomsList.some(r => r.room_id === rid);

          if (!existsOnBackend) {
            console.log(`[useChatRoom] Registering room "${rid}" on backend API...`);
            await apiClient.post(
              API_ENDPOINTS.chats.rooms,
              {
                sender_id:   String(currentUserUid),
                receiver_id: String(otherUserUid),
                room_id:     rid,
              },
              { suppressToast: true }
            );
          }
        } catch (apiErr) {
          console.warn('[useChatRoom] Backend room registration failed:', apiErr?.message);
        }
      })();

      if (!isMounted) return;

      setRoomId(rid);

      // ── Step 3: Load last BATCH_SIZE messages (one-time getDocs) ────────────
      const messagesRef = collection(db, 'chats', rid, 'messages');
      let initialMessages = [];
      let oldestDoc = null;
      let newestDoc = null;

      try {
        const initialQ = query(messagesRef, orderBy('timestamp', 'asc'), limitToLast(BATCH_SIZE));
        let initialSnap;

        // Try reading from the local IndexedDB cache first for instant load
        try {
          initialSnap = await getDocsFromCache(initialQ);
        } catch (e) {
          // Cache empty or failed, fallback to server
        }

        // If cache missed or is empty, fetch from server
        if (!initialSnap || initialSnap.empty) {
          initialSnap = await getDocs(initialQ);
        }

        initialMessages = initialSnap.docs.map(d => ({
          id: d.id,
          ...d.data(),
          timestamp: d.data().timestamp?.toDate?.()?.toISOString?.() ?? null,
        }));

        oldestDoc = initialSnap.docs[0] || null;
        newestDoc = initialSnap.docs[initialSnap.docs.length - 1] || null;

        console.log(`[useChatRoom] Loaded ${initialMessages.length} initial messages.`);
      } catch (fetchErr) {
        if (fetchErr?.code === 'permission-denied') {
          console.warn(`[useChatRoom] Permission denied fetching messages for "${rid}". Showing empty.`);
        } else {
          console.error('[useChatRoom] Error fetching initial messages:', fetchErr?.message);
        }
      }

      if (!isMounted) return;

      setMessages(initialMessages);
      oldestDocRef.current = oldestDoc;
      newestDocRef.current = newestDoc;
      setHasMore(initialMessages.length >= BATCH_SIZE);

      // ── Step 4a: Listener for NEW messages only (startAfter newestDoc) ─────
      console.log(`[useChatRoom] Attaching new-messages listener after doc:`, newestDoc?.id);

      let liveQ;
      if (newestDoc) {
        liveQ = query(messagesRef, orderBy('timestamp', 'asc'), startAfter(newestDoc));
      } else {
        liveQ = query(messagesRef, orderBy('timestamp', 'asc'));
      }

      const unsubNew = onSnapshot(
        liveQ,
        snapshot => {
          if (!isMounted) return;

          snapshot.docChanges().forEach(change => {
            if (change.type !== 'added') return; // edits/deletes handled by unsubChanges

            const msgData = change.doc.data();
            const msg = {
              id: change.doc.id,
              ...msgData,
              timestamp: msgData.timestamp?.toDate?.()?.toISOString?.() ?? null,
            };

            // Mark as read immediately since this screen is active
            if (String(msg.sender_uid) !== String(currentUserUid)) {
              updateDoc(doc(db, 'chats', rid), {
                [`unreadCounts.${String(currentUserUid)}`]: 0
              }).catch(() => {});
            }
            newestDocRef.current = change.doc;
            setMessages(prev => {
              if (prev.find(m => m.id === msg.id)) return prev;
              return [...prev, msg];
            });
          });

          setStatus('succeeded');
        },
        err => {
          if (!isMounted) return;
          if (err?.code === 'permission-denied') {
            console.warn(`[useChatRoom] Permission denied on room "${rid}". Showing empty chat.`);
            setMessages([]);
            setStatus('succeeded');
          } else {
            console.error('🔴 Firestore snapshot error (new-msg listener):', err?.code, err?.message);
            setError(err.message);
            setStatus('failed');
          }
        }
      );

      // ── Step 4b: Listener for EDITS and DELETES on ALL messages ─────────────
      // Listens to the full collection but only processes 'modified' and 'removed'.
      // This catches changes to paginated (older) messages that startAfter misses.
      console.log(`[useChatRoom] Attaching edit/delete listener for all messages.`);
      const changeQ = query(messagesRef, orderBy('timestamp', 'asc'));
      const unsubChanges = onSnapshot(
        changeQ,
        snapshot => {
          if (!isMounted) return;
          snapshot.docChanges().forEach(change => {
            const msgData = change.doc.data();
            const msg = {
              id: change.doc.id,
              ...msgData,
              timestamp: msgData.timestamp?.toDate?.()?.toISOString?.() ?? null,
            };

            if (change.type === 'modified') {
              const updatedMsg = { ...msg, isDeleted: msgData.isDeleted ?? false };
              setMessages(prev => prev.map(m => m.id === msg.id ? updatedMsg : m));
            } else if (change.type === 'removed') {
              setMessages(prev => prev.filter(m => m.id !== msg.id));
            }
            // 'added' intentionally ignored — handled by unsubNew
          });
        },
        err => {
          if (!isMounted) return;
          console.warn('[useChatRoom] Edit/delete listener error:', err?.code, err?.message);
        }
      );

      // Wrap both unsubs into one cleanup ref
      unsubscribeRef.current = () => { unsubNew(); unsubChanges(); };
      if (isMounted) setStatus('succeeded');
    };

    init().catch(err => {
      if (isMounted) {
        console.error('[useChatRoom] Unexpected init error:', err?.code, err?.message);
        setMessages([]);
        setStatus('succeeded');
      }
    });

    return () => {
      isMounted = false;
      detachListener();
    };
  }, [currentUserUid, otherUserUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load older messages (scroll-up pagination) ─────────────────────────────
  const loadMore = useCallback(async () => {
    if (!roomId || loadingOlder || !hasMore || !oldestDocRef.current) return;

    setLoadingOlder(true);
    try {
      const messagesRef = collection(db, 'chats', roomId, 'messages');
      const olderQ = query(
        messagesRef,
        orderBy('timestamp', 'asc'),
        endBefore(oldestDocRef.current),
        limitToLast(BATCH_SIZE)
      );
      const snap = await getDocs(olderQ);
      const older = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().timestamp?.toDate?.()?.toISOString?.() ?? null,
      }));

      if (older.length === 0) {
        setHasMore(false);
      } else {
        setMessages(prev => [...older, ...prev]);
        oldestDocRef.current = snap.docs[0];
        if (older.length < BATCH_SIZE) setHasMore(false);
      }
    } catch (err) {
      console.error('[useChatRoom] loadMore error:', err);
    } finally {
      setLoadingOlder(false);
    }
  }, [roomId, loadingOlder, hasMore]);

  // ── Send a message ──────────────────────────────────────────────────────────
  const sendMessage = useCallback(
    async (content) => {
      if (!content?.trim() || !currentUserUid || !otherUserUid) {
        console.warn('⚠️ [sendMessage] Skipped: Empty content or missing UIDs.');
        return;
      }

      const effectiveRoomId = roomId || getRoomId(currentUserUid, otherUserUid);
      const messagesRef     = collection(db, 'chats', effectiveRoomId, 'messages');
      const roomRef         = doc(db, 'chats', effectiveRoomId);

      console.log(`✉️ [sendMessage] Sending to room "${effectiveRoomId}"...`);

      try {
        const payload = {
          content:      content.trim(),
          sender_uid:   currentUserUid,
          receiver_uid: otherUserUid,
          timestamp:    serverTimestamp(),
          edited:       false,
          isDeleted:    false,
        };

        const docRef = await addDoc(messagesRef, payload);
        console.log(`🚀 [sendMessage] Delivered! ID: "${docRef.id}"`);

        await updateDoc(roomRef, {
          message:      content.trim(),
          last_message: content.trim(),
          last_time:    serverTimestamp(),
          [`unreadCounts.${String(otherUserUid)}`]: increment(1)
        });
        console.log('✅ [sendMessage] Room preview updated and unread incremented.');
      } catch (err) {
        console.error('❌ [sendMessage] Error:', err?.code, err?.message);
      }
    },
    [roomId, currentUserUid, otherUserUid]
  );

  // ── Edit a message ──────────────────────────────────────────────────────────
  const editMessage = useCallback(
    async (messageId, newContent) => {
      if (!roomId || !messageId || !newContent?.trim()) return;
      console.log(`✏️ [editMessage] Editing message "${messageId}"...`);
      try {
        const msgRef = doc(db, 'chats', roomId, 'messages', messageId);
        await updateDoc(msgRef, { content: newContent.trim(), edited: true });
        console.log('🚀 [editMessage] Updated.');
        // Check if this was the last message locally (bypasses serverTimestamp sorting issues)
        const isLatest = messages.length > 0 && messages[messages.length - 1].id === messageId;
        if (isLatest) {
          const roomRef = doc(db, 'chats', roomId);
          await updateDoc(roomRef, { last_message: newContent.trim(), message: newContent.trim() });
          console.log('🚀 Room preview synced for edit.');
        }
      } catch (err) {
        console.error('❌ [editMessage] Failed:', err?.code, err?.message);
      }
    },
    [roomId, messages]
  );

  // ── Soft delete a message ───────────────────────────────────────────────────
  const deleteMessage = useCallback(
    async (messageId) => {
      if (!roomId || !messageId) return;
      console.log(`🗑️ [deleteMessage] Soft-deleting "${messageId}"...`);
      try {
        const msgRef = doc(db, 'chats', roomId, 'messages', messageId);
        await updateDoc(msgRef, { content: 'This message was deleted', isDeleted: true });
        console.log('🚀 [deleteMessage] Deleted.');
        // Check if this was the last message locally (bypasses serverTimestamp sorting issues)
        const isLatest = messages.length > 0 && messages[messages.length - 1].id === messageId;
        if (isLatest) {
          const roomRef = doc(db, 'chats', roomId);
          await updateDoc(roomRef, { last_message: 'This message was deleted', message: 'This message was deleted' });
          console.log('🚀 Room preview synced for delete.');
        }
      } catch (err) {
        console.error('❌ [deleteMessage] Failed:', err?.code, err?.message);
      }
    },
    [roomId, messages]
  );

  return {
    roomId,
    messages,
    status,
    error,
    loadingOlder,
    hasMore,
    loadMore,
    sendMessage,
    editMessage,
    deleteMessage,
  };
}
